import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { loginUser } from "@/lib/api/login.api"
import type { LoginFormValues } from "@/lib/schemas/auth.schema"
import { useAuthStore } from "@/store/auth.store"
import { claimGuestConversations } from "@/lib/copilot/claim"

import { toast } from "sonner"

export function useLogin() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setAuth = useAuthStore((state) => state.setAuth)

  return useMutation({
    mutationFn: (credentials: LoginFormValues) => loginUser(credentials),
    onSuccess: (data, variables) => {
      // Attach the submitted role so the store knows it (backend returns role in user obj too)
      const enrichedData = {
        ...data,
        user: { ...data.user, role: variables.role },
      }
      setAuth(enrichedData)

      // Move anonymous conversations (created with the browser fingerprint)
      // onto this account now that the user is authenticated.
      void claimGuestConversations(queryClient)

      if (variables.role === "ADMIN") {
        navigate({ to: "/dashboard" })
      } else if (variables.role === "SELLER") {
        const ep = data.user.employeeProfile

        if (!ep?.id) {
          // No profile yet → profile setup form
          navigate({ to: "/seller-profile-setup" })
        } else if (!ep.companyId) {
          // Profile done but no company linked yet → company setup form
          navigate({ to: "/seller-company-setup" })
        } else {
          // Both profile and company exist → straight to dashboard
          navigate({ to: "/dashboard" })
        }
      } else {
        const buyer = data.user.buyer

        if (!buyer?.id) {
          navigate({ to: "/buyer-profile-setup" })
        } else {
          navigate({ to: "/dashboard" })
        }
      }
    },
    onError: (error: Error) => {
      console.error("Login failed:", error.message)
      toast.error(error.message, { id: error.message })
    },
  })
}
